import { agreementSigned } from '../domain/agreement';
import { inspectionServiceOf } from '../domain/inspectionService';
import { reportFileName, type V2ReportRecord } from '../domain/report';
import { renderAgreementDocumentHtml } from '../report/agreementHtml';
import type { PdfRenderer } from '../report/pdf';
import { shareReportFile } from '../report/share';
import { currentAgreementPdf } from './agreementPdfStore';
import { formalEligibilityBlockedFor } from './formalWorkflow';
import { inspectionStore } from './inspectionStore';

// The signed agreement as its OWN PDF, on demand (Round 2 §C / §AK): regenerated from the record
// whenever the inspector wants to view / share it — before, during or after the inspection — and
// stored as a separate report record at final report time (src/state/reportStore.ts). Never merged
// into the inspection report. It is the document the client signed — in the language chosen for the
// client (Owner 2026-10-28; Chinese then English for one signed under an older copy) — so it does not
// depend on the App Language.
export type AgreementPdfOutcome = { ok: true; fileName: string; bytes: ArrayBuffer; pageCount: number } | { ok: false; reason: 'notFound' | 'unsigned' | 'blocked' | 'renderFailed'; detail: string };

export async function buildAgreementPdfAction(inspectionId: string, renderer: PdfRenderer, now: number = Date.now()): Promise<AgreementPdfOutcome> {
  const inspection = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!inspection) return { ok: false, reason: 'notFound', detail: '' };
  // NY F1 Round 1 (§15.1 effect 3): the 197-4.2(a) clauses are not printed as if a home inspection
  // for an inspection the ordinary New York workflow does not cover. Checked before "unsigned"
  // because eligibility is the stronger and prior statement. Sharing goes through this builder, so
  // both the build and the share path are closed by this one line.
  // ELIGIBILITY only (X-1): "not signed yet" is the next line's answer, not a blocked workflow.
  if (formalEligibilityBlockedFor(inspection)) return { ok: false, reason: 'blocked', detail: '' };
  if (!agreementSigned(inspection.agreement)) return { ok: false, reason: 'unsigned', detail: '' };
  try {
    // Owner 2026-10-21: the PDF made when both parties signed, while the agreement is unchanged since.
    const pdf = (await currentAgreementPdf(inspection)) ?? (await renderer.render(renderAgreementDocumentHtml(inspection, now)));
    return { ok: true, fileName: reportFileName(inspection.property.address, now, 'agreement', inspection.agreement.demo, inspectionServiceOf(inspection.property)), bytes: pdf.bytes, pageCount: pdf.pageCount };
  } catch (raw) {
    return { ok: false, reason: 'renderFailed', detail: raw instanceof Error ? raw.message : String(raw) };
  }
}

export async function shareAgreementPdfAction(inspectionId: string, renderer: PdfRenderer): Promise<AgreementPdfOutcome | { ok: false; reason: 'shareFailed'; detail: string }> {
  const built = await buildAgreementPdfAction(inspectionId, renderer);
  if (!built.ok) return built;
  const record: Pick<V2ReportRecord, 'fileName'> = { fileName: built.fileName };
  const outcome = await shareReportFile(record as V2ReportRecord, new Blob([new Uint8Array(built.bytes)], { type: 'application/pdf' }));
  if (outcome.code === 'failed') return { ok: false, reason: 'shareFailed', detail: outcome.detail };
  return built;
}
